export type EmailGroupingMode = 'combined' | 'grouped' | 'individual_draft' | 'individual_review' | 'individual'

export type Settings = {
  p_track_internal_emails: boolean
  p_include_internal_in_narratives: boolean
  sent_emails: boolean
  minimum_units: boolean
  track_after_exported: boolean
  insurance_defense_mode: boolean
  check_for_red_words: boolean
  p_group_include_units: boolean
  paralegal_mode: boolean
  abbreviations_enabled: boolean
  word_count: number
  time_to_track_emails: number
  time_to_track_docs: number
  p_time_to_track_on_browser_s: number
  p_browser_session_inactivity_threshold_m: number
  p_time_to_track_meetings_s: number
  prof_type: string
  practice_type: string
  hardcoded_matter: string
  auto_populate_last_used_matter: boolean
  last_used_matter: string
  add_page_numbers: boolean
  email_grouping_mode: EmailGroupingMode
  p_narrative_prompt: string
  p_group_prompt: string
  p_merge_prompt: string
  p_collapse_prompt: string
  /**
   * Per-timekeeper default codes. null means "no default" — distinct from a code
   * that exists but isn't valid for the matter on an entry.
   */
  p_default_task_code: string | null
  p_default_activity_code: string | null
  /** Forces the defaults in the prediction pipeline. Manual entry pre-fills regardless. */
  p_hardcode_codes: boolean
}

export const defaultSettings: Settings = {
  p_track_internal_emails: true,
  p_include_internal_in_narratives: false,
  sent_emails: false,
  minimum_units: false,
  track_after_exported: true,
  insurance_defense_mode: false,
  check_for_red_words: false,
  p_group_include_units: true,
  paralegal_mode: false,
  abbreviations_enabled: true,
  word_count: 30,
  time_to_track_emails: 20,
  time_to_track_docs: 30,
  p_time_to_track_on_browser_s: 20,
  p_browser_session_inactivity_threshold_m: 30,
  p_time_to_track_meetings_s: 60,
  prof_type: '',
  practice_type: '',
  hardcoded_matter: '',
  auto_populate_last_used_matter: false,
  last_used_matter: '',
  add_page_numbers: true,
  email_grouping_mode: 'grouped',
  p_narrative_prompt: '',
  p_group_prompt: '',
  p_merge_prompt: '',
  p_collapse_prompt: '',
  p_default_task_code: null,
  p_default_activity_code: null,
  p_hardcode_codes: false,
}

export function parseSettings(raw: any): Settings {
  if (!raw) return defaultSettings
  return {
    p_track_internal_emails: raw.p_track_internal_emails != null ? !!raw.p_track_internal_emails : true,
    p_include_internal_in_narratives: !!raw.p_include_internal_in_narratives,
    sent_emails: !!raw.sent_emails,
    minimum_units: !!raw.minimum_units,
    track_after_exported: !!raw.track_after_exported,
    insurance_defense_mode: !!raw.insurance_defense_mode,
    check_for_red_words: !!raw.check_for_red_words,
    p_group_include_units: raw.p_group_include_units != null ? !!raw.p_group_include_units : true,
    paralegal_mode: !!raw.paralegal_mode,
    abbreviations_enabled: raw.abbreviations_enabled ?? true,
    word_count: raw.word_count ?? 30,
    time_to_track_emails: raw.time_to_track_emails ?? 20,
    time_to_track_docs: raw.time_to_track_docs ?? 30,
    p_time_to_track_on_browser_s: raw.p_time_to_track_on_browser_s ?? 20,
    p_browser_session_inactivity_threshold_m: raw.p_browser_session_inactivity_threshold_m ?? 30,
    p_time_to_track_meetings_s: raw.p_time_to_track_meetings_s ?? 60,
    prof_type: raw.prof_type ?? '',
    practice_type: raw.practice_type ?? '',
    hardcoded_matter: raw.hardcoded_matter ?? '',
    auto_populate_last_used_matter: !!raw.auto_populate_last_used_matter,
    last_used_matter: raw.last_used_matter ?? '',
    add_page_numbers: raw.add_page_numbers != null ? !!raw.add_page_numbers : true,
    email_grouping_mode: (['combined', 'grouped', 'individual_draft', 'individual_review', 'individual'] as const).includes(raw.email_grouping_mode)
      ? raw.email_grouping_mode
      : 'grouped',
    p_narrative_prompt: raw.p_narrative_prompt ?? '',
    p_group_prompt: raw.p_group_prompt ?? '',
    p_merge_prompt: raw.p_merge_prompt ?? '',
    p_collapse_prompt: raw.p_collapse_prompt ?? '',
    p_default_task_code: raw.p_default_task_code ?? null,
    p_default_activity_code: raw.p_default_activity_code ?? null,
    p_hardcode_codes: !!raw.p_hardcode_codes,
  }
}
